// src/pages/StaffPerformanceReportPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, getDocs, orderBy, Timestamp, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiPrinter, FiDownload, FiAward, FiTool } from 'react-icons/fi';
import styles from '../styles/StaffPerformanceReportPage.module.css';

// --- Helper Function Definitions ---
const parseDateSafely = (dateValue) => {
  if (!dateValue) return null;
  try {
    if (dateValue instanceof Timestamp) { return dateValue.toDate(); }
    if (typeof dateValue === 'string') {
      const dateString = (dateValue.length === 10 && !dateValue.includes('T'))
        ? dateValue + 'T00:00:00Z'
        : dateValue;
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date;
    }
    if (dateValue instanceof Date && !isNaN(dateValue)) { return dateValue; }
    return null;
  } catch {
    return null;
  }
};

const isDateInRange = (dateFieldValue, rangeStart, rangeEnd) => {
  if (rangeStart === null && rangeEnd === null) return !!dateFieldValue;
  if (!dateFieldValue) return false;
  const dateToTest = parseDateSafely(dateFieldValue);
  if (!dateToTest) return false;
  if (rangeStart && dateToTest < rangeStart) return false;
  if (rangeEnd && dateToTest > rangeEnd) return false;
  return true;
};

const getDateString = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
// --- End Helper Function Definitions ---

const managerAccessRoles = ['Manager', 'Accounts Manager'];

const StaffPerformanceReportPage = () => {
  const { currentUser, userRole, loading: authLoading } = useAuth();
  const [allLogs, setAllLogs] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [reportDateFilter, setReportDateFilter] = useState('last7');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [sendingNotificationTo, setSendingNotificationTo] = useState(null);
  const [search, setSearch] = useState('');

  const isMounted = useRef(true);
  const messageTimeoutRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (successMessage || error) {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = setTimeout(() => {
        if (isMounted.current) {
          setSuccessMessage('');
          setError('');
        }
      }, 3000);
    }
    return () => { if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current); };
  }, [successMessage, error]);

  useEffect(() => {
    if (authLoading || (userRole && !managerAccessRoles.includes(userRole))) return;

    const fetchData = async () => {
      if (!isMounted.current) return;
      setLoadingData(true);
      setError('');
      try {
        // Employees (exclude trainees)
        const usersCol = collection(db, 'users');
        const usersQuery = query(usersCol, orderBy('name', 'asc'));
        const usersSnapshot = await getDocs(usersQuery);
        if (!isMounted.current) return;
        const fetchedEmployees = usersSnapshot.docs
          .map(doc => ({
            id: doc.id,
            name: doc.data().name || doc.data().email,
            role: doc.data().role || 'Unknown',
          }))
          .filter(emp => (emp.role || '').toLowerCase() !== 'trainee');
        setAllEmployees(fetchedEmployees);

        // All daily logs
        const logsCol = collection(db, 'dailyLogs');
        const logsQuery = query(logsCol, orderBy('timestamp', 'desc'));
        const logsSnapshot = await getDocs(logsQuery);
        if (!isMounted.current) return;
        const fetchedLogs = logsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllLogs(fetchedLogs);
      } catch (err) {
        console.error('Error fetching report data:', err);
        if (isMounted.current) setError('Failed to load report data. A Firestore index may be required.');
      } finally {
        if (isMounted.current) setLoadingData(false);
      }
    };
    fetchData();
  }, [userRole, authLoading]);

  const reportDateRange = useMemo(() => {
    const today = new Date();
    let start = new Date();
    let end = new Date();
    end.setHours(23, 59, 59, 999);
    switch (reportDateFilter) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        start = new Date(today); start.setDate(today.getDate() - 1); start.setHours(0, 0, 0, 0);
        end = new Date(today); end.setDate(today.getDate() - 1); end.setHours(23, 59, 59, 999);
        break;
      case 'last7':
        start = new Date(today); start.setDate(today.getDate() - 6); start.setHours(0, 0, 0, 0);
        break;
      case 'last28':
        start = new Date(today); start.setDate(today.getDate() - 27); start.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          start = parseDateSafely(`${customStartDate}T00:00:00`);
          end = parseDateSafely(`${customEndDate}T23:59:59.999`);
          if (!start || !end || end < start) return { start: null, end: null, isValid: false, filterType: 'custom' };
        } else {
          return { start: null, end: null, isValid: false, filterType: 'custom' };
        }
        break;
      case 'maximum':
      default:
        return { start: null, end: null, isValid: true, filterType: 'maximum' };
    }
    return { start, end, isValid: true, filterType: reportDateFilter };
  }, [reportDateFilter, customStartDate, customEndDate]);

  // Build per-employee stats, including auto-zero count and correct 0-rating handling
  const reportData = useMemo(() => {
    if (loadingData || !allEmployees.length || !reportDateRange.isValid) return [];

    const { start: rangeStart, end: rangeEnd } = reportDateRange;

    const rows = allEmployees.map(emp => {
      const employeeLogsInRange = allLogs.filter(
        log => log.userId === emp.id && isDateInRange(log.timestamp, rangeStart, rangeEnd)
      );

      const logsSubmitted = employeeLogsInRange.length;

      // Count rated logs correctly (include rating === 0)
      const ratedLogs = employeeLogsInRange.filter(
        log => typeof log.rating === 'number' && !Number.isNaN(log.rating)
      );
      const logsRatedCount = ratedLogs.length;

      // Auto-zero detection (support either explicit flag or heuristic)
      const autoZeroCount = employeeLogsInRange.filter(log =>
        log.autoZero === true ||
        (typeof log.rating === 'number' && log.rating === 0 && (!log.logText || !String(log.logText).trim()))
      ).length;

      // Average over the rated logs (0 included naturally)
      const sumOfRatings = ratedLogs.reduce((acc, log) => acc + Number(log.rating), 0);
      const averageRating = logsRatedCount > 0 ? sumOfRatings / logsRatedCount : null;

      // Sort key: unrated at bottom
      const performanceScore = averageRating === null ? -1 : averageRating;

      return {
        employeeId: emp.id,
        employeeName: emp.name,
        employeeRole: emp.role,
        stats: {
          logsSubmitted,
          logsRated: logsRatedCount,
          autoZeroCount,
          averageRating,
          performanceScore,
        },
      };
    });

    // Sort by performance descending (unrated last)
    rows.sort((a, b) => (b.stats.performanceScore ?? -1) - (a.stats.performanceScore ?? -1));
    return rows;
  }, [allLogs, allEmployees, reportDateRange, loadingData]);

  const topPerformer = useMemo(
    () => (reportData.length > 0 && reportData[0].stats.averageRating !== null ? reportData[0] : null),
    [reportData]
  );

  const needsImprovement = useMemo(() => {
    const rated = reportData.filter(r => r.stats.averageRating !== null);
    return rated.length > 1 ? rated[rated.length - 1] : null;
  }, [reportData]);

  const summary = useMemo(() => {
    const rated = reportData.filter((r) => r.stats.averageRating !== null);
    const avg = rated.length ? rated.reduce((s, r) => s + r.stats.averageRating, 0) / rated.length : null;
    const logsSubmitted = reportData.reduce((s, r) => s + (r.stats.logsSubmitted || 0), 0);
    const autoZeros = reportData.reduce((s, r) => s + (r.stats.autoZeroCount || 0), 0);
    return { staff: reportData.length, avg, logsSubmitted, autoZeros };
  }, [reportData]);

  const filteredReport = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return reportData;
    return reportData.filter((r) => `${r.employeeName || ''} ${r.employeeRole || ''}`.toLowerCase().includes(term));
  }, [reportData, search]);

  const todayForDateInput = useMemo(() => getDateString(new Date()), []);

  const handlePrint = () => window.print();

  const handleExport = useCallback(() => {
    if (!reportData.length) return;
    const headers = ["Employee Name", "Role", "Logs Submitted", "Logs Rated", "Auto 0s", "Average Rating"];
    const rows = reportData.map(item => [
      `"${String(item.employeeName || '').replace(/"/g, '""')}"`,
      `"${String(item.employeeRole || '').replace(/"/g, '""')}"`,
      item.stats.logsSubmitted,
      item.stats.logsRated,
      item.stats.autoZeroCount,
      item.stats.averageRating !== null ? item.stats.averageRating.toFixed(2) : 'N/A'
    ].join(','));
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `staff_rating_report_${getDateString(new Date())}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [reportData]);

  const handleSendNotification = useCallback(async (employeeId, employeeName, type) => {
    if (!currentUser) { setError("You must be logged in."); return; }
    const managerName = currentUser.displayName || currentUser.email;

    let message, confirmMessage, notificationType;
    if (type === 'congratulation') {
      notificationType = 'performance_congratulation';
      message = `${managerName} sent you a commendation for your excellent performance! 🎉`;
      confirmMessage = `Send a congratulatory message to ${employeeName}?`;
    } else {
      notificationType = 'performance_warning';
      message = `${managerName} sent a performance warning. Please review your recent tasks and stats.`;
      confirmMessage = `Send a performance warning to ${employeeName}?`;
    }

    if (window.confirm(confirmMessage)) {
      if (!isMounted.current) return;
      setSendingNotificationTo(employeeId);
      setError(''); setSuccessMessage('');
      try {
        await addDoc(collection(db, 'notifications'), {
          userId: employeeId,
          message,
          link: '/dashboard',
          timestamp: serverTimestamp(),
          isRead: false,
          createdBy: currentUser.uid,
          createdByName: managerName,
          type: notificationType
        });
        if (isMounted.current) setSuccessMessage(`Notification sent to ${employeeName}.`);
      } catch (err) {
        console.error("Error sending notification:", err);
        if (isMounted.current) setError(`Failed to send notification to ${employeeName}.`);
      } finally {
        if (isMounted.current) setSendingNotificationTo(null);
      }
    }
  }, [currentUser]);

  if (authLoading || loadingData) {
    return <div className={styles.loadingIndicator}>Loading Staff Performance Data...</div>;
  }
  if (error) {
    return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error}</div>;
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.reportHeader}>
        <div className={styles.headerLeft}>
          <h2 className={styles.pageTitle}>Staff Performance</h2>
          <p className={styles.subtitle}>Based on daily work-log ratings for the selected period.</p>
        </div>
        <div className={styles.actions}>
          <button onClick={handlePrint} className={styles.actionButton}><FiPrinter /> Print</button>
          <button onClick={handleExport} className={styles.actionButton} disabled={reportData.length === 0}><FiDownload /> Export CSV</button>
        </div>
      </div>

      {successMessage && <p className={styles.successMessage}>{successMessage}</p>}
      {error && !loadingData && <p className={styles.errorMessage}>{error}</p>}

      <div className={styles.filtersContainer}>
        <label htmlFor="report-date-filter" className={styles.dateFilterLabel}>Report For:</label>
        <select
          id="report-date-filter"
          value={reportDateFilter}
          onChange={(e) => setReportDateFilter(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="last7">Last 7 Days</option>
          <option value="last28">Last 28 Days</option>
          <option value="custom">Custom Range</option>
          <option value="maximum">All Time</option>
        </select>

        {reportDateFilter === 'custom' && (
          <div className={styles.customDateInputs}>
            <input
              type="date"
              aria-label="Custom start date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className={styles.dateInput}
              max={customEndDate || todayForDateInput}
            />
            <span>to</span>
            <input
              type="date"
              aria-label="Custom end date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className={styles.dateInput}
              min={customStartDate || undefined}
              max={todayForDateInput}
            />
          </div>
        )}
      </div>

      {/* Summary */}
      <div className={styles.statsRow}>
        <div className={`${styles.statCard} ${styles.statStaff}`}>
          <div className={styles.statLabel}>Staff Reported</div>
          <div className={styles.statValue}>{summary.staff}</div>
        </div>
        <div className={`${styles.statCard} ${styles.statAvg}`}>
          <div className={styles.statLabel}>Avg. Rating</div>
          <div className={styles.statValue}>{summary.avg !== null ? summary.avg.toFixed(2) : '—'}</div>
        </div>
        <div className={`${styles.statCard} ${styles.statLogs}`}>
          <div className={styles.statLabel}>Logs Submitted</div>
          <div className={styles.statValue}>{summary.logsSubmitted}</div>
        </div>
        <div className={`${styles.statCard} ${styles.statZeros}`}>
          <div className={styles.statLabel}>Auto 0s</div>
          <div className={styles.statValue}>{summary.autoZeros}</div>
        </div>
      </div>

      {reportData.length > 0 && (topPerformer || needsImprovement) && (
        <div className={styles.performanceHighlights}>
          {topPerformer && (
            <div className={`${styles.highlightItem} ${styles.highlightTop}`}>
              <FiAward className={styles.highlightIcon} />
              <div>
                <span className={styles.highlightLabel}>Top Performer</span>
                <span className={styles.highlightName}>{topPerformer.employeeName}</span>
                <span className={styles.highlightMeta}>Avg. {topPerformer.stats.averageRating.toFixed(2)}</span>
              </div>
            </div>
          )}
          {needsImprovement && topPerformer?.employeeId !== needsImprovement.employeeId && (
            <div className={`${styles.highlightItem} ${styles.highlightNeeds}`}>
              <FiTool className={styles.highlightIcon} />
              <div>
                <span className={styles.highlightLabel}>Needs Most Improvement</span>
                <span className={styles.highlightName}>{needsImprovement.employeeName}</span>
                <span className={styles.highlightMeta}>Avg. {needsImprovement.stats.averageRating.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search staff by name or role…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.reportTableContainer}>
        <table className={styles.reportTable}>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Role</th>
              <th>Submitted</th>
              <th>Rated</th>
              <th>Auto 0s</th>
              <th>Avg. Rating</th>
              <th className={styles.actionsHead}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReport.length === 0 && (
              <tr>
                <td colSpan="7" className={styles.noDataMessage}>
                  {reportData.length === 0 ? 'No performance data found for the selected period.' : 'No staff match your search.'}
                </td>
              </tr>
            )}
            {filteredReport.map(({ employeeId, employeeName, employeeRole, stats }) => {
              const avg = stats.averageRating;
              const avgClass = typeof avg === 'number' ? (avg < 3 ? styles.statLate : (avg >= 4 ? styles.statGood : styles.statMid)) : '';
              return (
              <tr key={employeeId}>
                <td data-label="Employee">
                  <div className={styles.personCell}>
                    <div className={styles.avatar}>{(employeeName || '?').charAt(0).toUpperCase()}</div>
                    <div className={styles.personName}>{employeeName}</div>
                  </div>
                </td>
                <td data-label="Role">{employeeRole ? <span className={styles.roleChip}>{employeeRole}</span> : '—'}</td>
                <td data-label="Submitted">{stats.logsSubmitted}</td>
                <td data-label="Rated">{stats.logsRated}</td>
                <td data-label="Auto 0s">{stats.autoZeroCount}</td>
                <td data-label="Avg. Rating">
                  {avg !== null
                    ? <span className={`${styles.ratingBadge} ${avgClass}`}>{avg.toFixed(2)}</span>
                    : <span className={styles.muted}>N/A</span>}
                </td>
                <td data-label="Actions" className={styles.actionsCell}>
                  <button
                    className={`${styles.smallActionButton} ${styles.congratsButton}`}
                    onClick={() => handleSendNotification(employeeId, employeeName, 'congratulation')}
                    disabled={sendingNotificationTo === employeeId}
                    title="Send a congratulatory message"
                  >
                    🎉 Congratulate
                  </button>
                  <button
                    className={`${styles.smallActionButton} ${styles.warningButton}`}
                    onClick={() => handleSendNotification(employeeId, employeeName, 'warning')}
                    disabled={sendingNotificationTo === employeeId}
                    title="Send a performance warning"
                  >
                    ⚠️ Warn
                  </button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StaffPerformanceReportPage;
